import { and, eq, inArray } from "drizzle-orm";
import * as Sentry from "@sentry/browser";
import cuid from "cuid";
import type React from "react";
import { createContext, useCallback, useContext } from "react";

import type { AccountEventRequest, AccountEventType, PublishBroadcastResponse } from "@storyboard/web-client";
import { client } from "@/config";
import { createLogger } from "@/logger";

const log = createLogger("events");

import { db } from "@/db/db";
import { accountEvent as accountEventTable, item } from "@/db/schema";
import { upsertAccountEvent } from "@/models/repositories/AccountEvent";
import { UxContext } from "@/models/UxStateProvider";
import { enqueueCalculateUnreads } from "./actions/calculateUnreads";
import { emitTableUpdates } from "./eventing/TableEventManager";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";
import { TelemetryContext } from "./TelemetryProvider";

// Batch handler for hasRegisteredSeenEvent updates
const seenEventBatch: Set<string> = new Set();
let seenEventBatchTimer: NodeJS.Timeout | null = null;
const SEEN_EVENT_BATCH_DELAY = 100; // 100ms

function processBatchedSeenEvents() {
  if (seenEventBatch.size === 0) {
    seenEventBatchTimer = null;
    return;
  }

  const itemIds = Array.from(seenEventBatch);
  seenEventBatch.clear();
  seenEventBatchTimer = null;

  db.update(item).set({ hasRegisteredSeenEvent: true }).where(inArray(item.id, itemIds)).execute();
}

let accountEventEmitTimer: NodeJS.Timeout | null = null;
const ACCOUNT_EVENT_EMIT_DELAY = 500;

function enqueueAccountEventEmit() {
  if (accountEventEmitTimer) clearTimeout(accountEventEmitTimer);
  accountEventEmitTimer = setTimeout(() => {
    accountEventEmitTimer = null;
    emitTableUpdates(["account_event"]);
  }, ACCOUNT_EVENT_EMIT_DELAY);
}

function enqueueBatchedSeenEvent(itemId: string) {
  seenEventBatch.add(itemId);

  if (!seenEventBatchTimer) {
    seenEventBatchTimer = setTimeout(processBatchedSeenEvents, SEEN_EVENT_BATCH_DELAY);
  }
}

type ActionState = {
  accountEvent?: (event: AccountEventType, data: any) => void;
  publishBroadcast?: (params: {
    workspaceId: string;
    contentId: string;
    workspaceMembershipIds?: string[];
    feedIds?: string[];
    inputText?: string;
  }) => Promise<PublishBroadcastResponse>;
  sendFeedback?: (message: string) => Promise<void>;
};

export const ActionContext = createContext<ActionState>({});

type Props = {
  children: React.ReactNode | React.ReactNode[];
};

const ActionsProvider = ({ children }: Props) => {
  const { userReadOnlyMode } = useContext(UxContext);
  const { trackAction, finishAction } = useContext(TelemetryContext);
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);

  const accountEvent = useCallback(
    async (name: AccountEventType, data: any) => {
      if (userReadOnlyMode === true) {
        log.debug("Read only mode enabled, not sending event");
        return;
      }

      if (!myCurrentWorkspaceMembership) return;
      const myAcocuntId = myCurrentWorkspaceMembership.accountId;
      if (data.feedId && data.feedItemId) {
        // Check if item already has the flag set to avoid unnecessary DB queries
        const itemRecord = await db.query.item
          .findFirst({
            columns: { id: true, hasRegisteredSeenEvent: true },
            where: eq(item.id, data.feedItemId),
          })
          .execute();

        if (itemRecord?.hasRegisteredSeenEvent) {
          // Item already has the flag set, nothing to do
          return;
        }

        const existingEvent = await db.query.accountEvent
          .findFirst({
            where: and(
              eq(accountEventTable.name, name),
              eq(accountEventTable.feedId, data.feedId),
              eq(accountEventTable.itemId, data.feedItemId),
              eq(accountEventTable.accountId, myAcocuntId),
            ),
          })
          .execute();

        if (existingEvent) {
          log.debug("Already sent event", existingEvent);
          enqueueBatchedSeenEvent(data.feedItemId);
          return;
        }
      }
      const event: AccountEventRequest = {
        id: cuid(),
        name,
        ...data,
        timestamp: new Date(Date.now()).toISOString(),
        accountId: myAcocuntId,
      };
      client
        .createAccountEvent({
          ...event,
        })
        .then(() => {});
      await upsertAccountEvent(event, true);
      enqueueAccountEventEmit();
      enqueueCalculateUnreads({
        feedIds: [event.feedId],
        membership: myCurrentWorkspaceMembership,
        alsoSetAsRead: true,
      });
    },
    [myCurrentWorkspaceMembership, userReadOnlyMode],
  );

  const publishBroadcast = useCallback(
    async ({
      workspaceId,
      contentId,
      workspaceMembershipIds,
      feedIds,
      inputText,
    }: {
      workspaceId: string;
      contentId: string;
      workspaceMembershipIds?: string[];
      feedIds?: string[];
      inputText?: string;
    }) => {
      try {
        trackAction(
          { action: "publish_broadcast", target: contentId },
          {
            workspaceId,
            workspaceMembershipIds,
            feedIds,
            inputText,
          },
        );
        const broadcast = await client.publishBroadcast({
          workspaceId,
          contentId,
          workspaceMembershipIds,
          feedIds,
          inputText,
        });
        finishAction({ action: "publish_broadcast", target: contentId });
        return broadcast;
      } catch (error) {
        finishAction({ action: "failed_publish_broadcast", target: contentId });
        Sentry.captureException(error);
        return Promise.reject(error);
      }
    },
    [trackAction, finishAction],
  );

  const sendFeedback = useCallback(async (message: string) => {
    const feedback = await client.uploadLogs({
      logs: "",
      userMessage: message,
    });

    return feedback;
  }, []);

  const actionState: ActionState = {
    accountEvent,
    publishBroadcast,
    sendFeedback,
  };

  return <ActionContext.Provider value={actionState}>{children}</ActionContext.Provider>;
};

export default ActionsProvider;
